import { existsSync, readFileSync, statSync } from 'node:fs';
import { join, resolve as resolvePath } from 'node:path';
import { extractCourseCopy, restoreEnglishCourseCopy, courseCopySourceText, collapseCourseCopyVariants, createCourseCopyKeyResolver } from './copy-contract.js';
import { prepareScriptDisplayCopy, finishScriptDisplayCopy } from './script/localization.js';
import { sharedScriptLocale, sharedScriptLocaleRevision } from './script/shared-copy.js';
import { SnapshotCache } from './snapshot-cache.js';

const PREFIX = '@course-copy:';
const localeCache = new SnapshotCache();

function read(file) {
  return existsSync(file) ? JSON.parse(readFileSync(file, 'utf8')) : {};
}

export function courseLocaleRevision(root, ui, { script = false } = {}) {
  return ['en', ui].map(lang => {
    const file = join(root, 'locales', lang + '.json');
    return existsSync(file) ? file + ':' + statSync(file).mtimeMs : '';
  }).join('|') + (script ? '|' + sharedScriptLocaleRevision(ui) : '');
}

export function courseLocale(root, ui = 'en') {
  const cacheKey = JSON.stringify([resolvePath(root), ui]);
  const cached = localeCache.get(cacheKey);
  if (process.env.NODE_ENV === 'production' && cached) return cached.locale;
  const revision = courseLocaleRevision(root, ui, { script: true });
  if (cached?.revision === revision) return cached.locale;
  const englishFile = read(join(root, 'locales/en.json'));
  const english = englishFile.strings || {};
  const translated = ui === 'en' ? {} : read(join(root, 'locales', ui + '.json')).strings || {};
  const resolveCopyKey = createCourseCopyKeyResolver(englishFile);
  const strings = { ...sharedScriptLocale('en').strings, ...english, ...(ui === 'en' ? {} : sharedScriptLocale(ui).strings), ...translated };
  const resolve = (text, domain = '', context = {}) => {
    if (typeof text !== 'string') return text;
    if (text.startsWith(PREFIX)) {
      const key = text.slice(PREFIX.length);
      if (!(key in strings)) throw Error('Missing course locale string: ' + key);
      return strings[key];
    }
    const source=courseCopySourceText(text,domain,context,englishFile.englishOverrides||{});
    return translated[resolveCopyKey(source, domain, context)] || source;
  };
  const hydrate = value => {
    if (typeof value === 'string') return value.startsWith(PREFIX) ? resolve(value) : value;
    if (Array.isArray(value)) return value.map(hydrate);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, hydrate(item)]));
    return value;
  };
  const hydrateSource = (value, prefix = 'course', id = '') => {
    const restored = restoreEnglishCourseCopy(value, english);
    const source = prefix === 'script' ? prepareScriptDisplayCopy(restored) : restored;
    const extractedStrings = {};
    const sourceDocument = extractCourseCopy(source, extractedStrings, id ? [prefix, id] : [prefix], {englishOverrides:englishFile.englishOverrides||{}, resolveCopyKey});
    const shared = prefix === 'script' ? { ...sharedScriptLocale('en').strings, ...sharedScriptLocale(ui).strings } : {};
    const sourceStrings = { ...english, ...extractedStrings, ...shared, ...translated };
    const fill = item => {
      if (typeof item === 'string' && item.startsWith(PREFIX)) {
        const key = item.slice(PREFIX.length);
        if (!(key in sourceStrings)) throw Error('Missing course locale string: ' + key);
        return sourceStrings[key];
      }
      if (Array.isArray(item)) return item.map(fill);
      if (item && typeof item === 'object') return Object.fromEntries(Object.entries(item).map(([key, child]) => [key, fill(child)]));
      return item;
    };
    const hydrated = collapseCourseCopyVariants(fill(sourceDocument));
    return prefix === 'script' ? finishScriptDisplayCopy(hydrated, ui) : hydrated;
  };
  const locale = { hydrate, hydrateSource, resolve, strings, lookups: hydrate(englishFile.lookups || {}) };
  localeCache.set(cacheKey, { revision, locale });
  return locale;
}
